import type { PartialBlock } from "@blocknote/core";

/**
 * Convert BlockNote blocks back to plain text (one line per block).
 *
 * Used by the meeting follow-up email tab to capture the user's in-sheet
 * edits so Copy and Open Draft operate on what the user actually sees,
 * not the original AI-generated text. Inverse of the sheets' local
 * `emailToBlocks` (paragraph-per-line), with pragmatic handling for list
 * items the user may add while editing.
 */
export function blocksToPlainText(blocks: PartialBlock[]): string {
  let numberedIndex = 0;
  return blocks
    .map((block) => {
      const text = inlineContentToText((block as { content?: unknown }).content);
      if (block.type === "numberedListItem") {
        numberedIndex += 1;
        return `${numberedIndex}. ${text}`;
      }
      numberedIndex = 0;
      if (block.type === "bulletListItem" || block.type === "checkListItem") {
        return `- ${text}`;
      }
      return text;
    })
    .join("\n");
}

/** Concatenate a block's inline content (text runs + link children) into a plain string. */
export function inlineContentToText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((item) => {
      if (!item || typeof item !== "object") return "";
      if ("text" in item) return String((item as { text?: unknown }).text ?? "");
      // Inline links carry their own nested content array
      if ("content" in item) return inlineContentToText((item as { content?: unknown }).content);
      return "";
    })
    .join("");
}
